import http from "./http";
import secureLocalStorage from "react-secure-storage";
import { API_URL, USE_MOCK } from "./config";
import { mockUser } from "./mock/auth";
import { toProfile } from "../utils/profile";

const apiUrl = `${API_URL}auth`;

export function getJWT() {
  return secureLocalStorage.getItem("accessToken");
}

export const isTokenValid = () => {
  const expiresAt = secureLocalStorage.getItem("expiresAt");
  if (!expiresAt) return false;

  const exp = Number(expiresAt);
  const currentTime = Math.floor(Date.now() / 1000);
  return exp > currentTime;
};

export function getUserLocal() {
  return secureLocalStorage.getItem("user");
}

function saveSession({ user, expires_in, expires_at, access_token }) {
  secureLocalStorage.setItem("user", user);
  secureLocalStorage.setItem("expiresIn", expires_in);
  secureLocalStorage.setItem("expiresAt", expires_at);
  secureLocalStorage.setItem("accessToken", access_token);
}

export function clearSession() {
  secureLocalStorage.removeItem("user");
  secureLocalStorage.removeItem("expiresAt");
  secureLocalStorage.removeItem("expiresIn");
  secureLocalStorage.removeItem("accessToken");
}

// Mock mode only: "karim.ahmed@x.com" -> { name: "Karim Ahmed", email }
const userFromEmail = (email = "") => {
  const name = email
    .split("@")[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
  return { email, ...(name && { name }) };
};

// Mock mode: REACT_APP_MOCK_NEW_DEVICE=true makes the first sign in ask for the new-device OTP (the code is 123456)
const MOCK_DEVICE_KEY = "mockDeviceRegistered";
const mockDeviceRegistered = () => {
  try {
    return localStorage.getItem(MOCK_DEVICE_KEY) === "true";
  } catch {
    return false;
  }
};

// -> the session ({ user, access_token, ... }), or { newDevice: { isEmailVerified, isPhoneVerified,
// isTwoFactorAuthenticationEnabled } } when the backend wants the new-device OTP first (the sign in then goes on
// with generateDeviceId + login again, see layouts/Login)
export async function login(formData) {
  if (USE_MOCK) {
    if (process.env.REACT_APP_MOCK_NEW_DEVICE === "true" && !mockDeviceRegistered()) {
      return { newDevice: { isEmailVerified: true, isPhoneVerified: false, isTwoFactorAuthenticationEnabled: true } };
    }
    const expiresIn = 8 * 60 * 60;
    const data = {
      user: { ...mockUser, ...userFromEmail(formData.email) },
      expires_in: expiresIn,
      expires_at: Math.floor(Date.now() / 1000) + expiresIn,
      access_token: "mock-access-token",
    };
    saveSession(data);
    return data;
  }

  const response = await http.post(`${apiUrl}/login`, formData);

  // 203: an unrecognized device - the backend wants an OTP before it issues tokens. The answer says which ways the
  // account has to receive one (email / phone / authenticator app).
  if (response.status === 203) {
    const { isEmailVerified, isPhoneVerified, isTwoFactorAuthenticationEnabled } = response.data || {};
    return {
      newDevice: {
        isEmailVerified: Boolean(isEmailVerified),
        isPhoneVerified: Boolean(isPhoneVerified),
        isTwoFactorAuthenticationEnabled: Boolean(isTwoFactorAuthenticationEnabled),
      },
    };
  }

  const data = response.data.payload;

  // saved in the same shape getMe() returns, so nothing in the header changes when the profile loads
  saveSession({ ...data, user: { ...data.user, ...toProfile(data.user) } });

  return data;
}

// After the new-device OTP was verified: the backend registers this browser (a cookie) so the next sign in passes
export async function generateDeviceId({ email }) {
  if (USE_MOCK) {
    try {
      localStorage.setItem(MOCK_DEVICE_KEY, "true");
    } catch {
      // storage unavailable - the mock asks again next time
    }
    return;
  }
  await http.post(`${apiUrl}/generate-device-id`, { email });
}

export async function logout() {
  try {
    if (!USE_MOCK) await http.delete(`${apiUrl}/logout`);
  } finally {
    clearSession();
  }
}

const auth = {
  login,
  logout,
  getJWT,
  getUserLocal,
  clearSession,
};

export default auth;
